import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost } from './client'

/**
 * Matricea Urgenta x Impact (ITIL), cerinta CEC L25.
 *
 * Doua fete:
 *   - setarile: `Settings → Easeedesk → Ticket priorities`, sub tabelul de
 *     prioritati (`/settings/easeedesk/priority_matrix`);
 *   - tichetele: formularul de tichet nou (`/easeedesk/priority-matrix`) si
 *     banda de pe fisa (`/easeedesk/tickets/{id}/priority-matrix`).
 *
 * Regula de calcul e una singura, pe server (`Application\Services\Easeedesk\
 * PriorityMatrix`). Ecranul o repeta numai ca sa ARATE prioritatea inainte de
 * salvare - `matrixPriority` de mai jos; ce se scrie hotaraste serverul.
 */

export type MatrixAxis = 'urgency' | 'impact'

export interface MatrixLevel {
  ID: number
  name: string
  sort_order?: number
  /** Numai la impact: de la cate lucruri atinse se sugereaza nivelul. */
  min_affected?: number | null
}

export interface MatrixCell {
  urgency_id: number
  impact_id: number
  priority_id: number
}

export interface MatrixPriority {
  value: number
  name: string
}

export interface PriorityMatrixSettings {
  ready: boolean
  enabled: boolean
  urgencies: MatrixLevel[]
  impacts: MatrixLevel[]
  cells: MatrixCell[]
  priorities: MatrixPriority[]
}

export interface MatrixSuggestion {
  affected: number
  impact_id: number | null
}

/** Ce primeste un formular de tichet: matricea furnizorului si dreptul omului. */
export interface TicketMatrix {
  enabled: boolean
  urgencies: MatrixLevel[]
  impacts: MatrixLevel[]
  cells: MatrixCell[]
  priorities: MatrixPriority[]
  canOverride: boolean
  suggestion: MatrixSuggestion | null
}

export interface TicketMatrixPage extends TicketMatrix {
  ticket: number
  editable: boolean
  ticketPriority: number
  current: {
    urgency_id: number | null
    impact_id: number | null
    computed_priority: number | null
    overridden: boolean
  } | null
}

export interface ReprioritizeAnswer {
  status: string
  ticket: number
  priority: number
  computed: number | null
  overridden: boolean
}

/** Campurile de formular, langa `ticket_priority`. */
export const URGENCY_FIELD = 'priority_urgency'
export const IMPACT_FIELD = 'priority_impact'

/** Prioritatea din tabel pentru o pereche; null = celula goala sau nivel lipsa. */
export function matrixPriority(matrix: Pick<TicketMatrix, 'enabled' | 'cells'> | undefined, urgency: number, impact: number): number | null {
  if (!matrix?.enabled || urgency < 1 || impact < 1) return null
  const cell = matrix.cells.find((c) => c.urgency_id === urgency && c.impact_id === impact)
  return cell && cell.priority_id > 0 ? cell.priority_id : null
}

export function priorityName(priorities: MatrixPriority[], id: number | null | undefined): string {
  if (!id) return ''
  return priorities.find((p) => p.value === id)?.name ?? String(id)
}

/* ---------------------------------------------------------------- setari */

const SETTINGS = '/settings/easeedesk/priority_matrix'

export function priorityMatrixSettingsQuery() {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'priority_matrix'],
    queryFn: () => apiGet<PriorityMatrixSettings>(SETTINGS),
  })
}

export function setMatrixEnabled(on: boolean) {
  return apiPost<{ ok?: boolean }>(`${SETTINGS}/enabled`, { enabled: on ? '1' : '0' })
}

export function seedMatrixDefaults() {
  return apiPost<{ ok?: boolean }>(`${SETTINGS}/defaults`, {})
}

export function addMatrixLevel(axis: MatrixAxis, name: string, sortOrder: number, minAffected: number | null) {
  return apiPost<{ ok?: boolean; ID?: number }>(`${SETTINGS}/levels`, {
    axis,
    name,
    sort_order: sortOrder,
    min_affected: minAffected === null ? '' : minAffected,
  })
}

export function updateMatrixLevel(id: number, name: string, sortOrder: number, minAffected: number | null) {
  return apiPost<{ ok?: boolean }>(`${SETTINGS}/levels/${id}`, {
    name,
    sort_order: sortOrder,
    min_affected: minAffected === null ? '' : minAffected,
  })
}

export function removeMatrixLevel(id: number) {
  return apiPost<{ ok?: boolean }>(`${SETTINGS}/levels/${id}/remove`, {})
}

export function setMatrixCell(urgencyId: number, impactId: number, priorityId: number) {
  return apiPost<{ ok?: boolean }>(`${SETTINGS}/cell`, {
    urgency_id: urgencyId,
    impact_id: impactId,
    priority_id: priorityId,
  })
}

/* ---------------------------------------------------------------- tichete */

/** Matricea pentru formularul de tichet nou; `ci` = CI-urile alese, pentru sugestie. */
export function createFormMatrixQuery(ci: number[] = []) {
  const params = new URLSearchParams()
  for (const id of ci) params.append('ci[]', String(id))
  const tail = params.toString()

  return queryOptions({
    queryKey: ['easeedesk', 'priority-matrix', tail],
    queryFn: () => apiGet<TicketMatrix>(`/easeedesk/priority-matrix${tail === '' ? '' : `?${tail}`}`),
  })
}

export function ticketMatrixQuery(ticket: number) {
  return queryOptions({
    queryKey: ['easeedesk', 'ticket', ticket, 'priority-matrix'],
    queryFn: () => apiGet<TicketMatrixPage>(`/easeedesk/tickets/${ticket}/priority-matrix`),
  })
}

export function reprioritize(ticket: number, urgency: number, impact: number, priority: number | null) {
  const body: Record<string, string> = {
    [URGENCY_FIELD]: String(urgency),
    [IMPACT_FIELD]: String(impact),
  }
  if (priority !== null && priority > 0) body.ticket_priority = String(priority)

  return apiPost<ReprioritizeAnswer>(`/easeedesk/tickets/${ticket}/priority-matrix`, body)
}
